import { VISA_SUBCLASSES, type VisaSubclass } from '@admitly/shared';
import type { PricingCourseRow } from '../hooks/usePricing.js';
import { canSeeQuotes } from './capabilities.js';

/**
 * The pure core of the quotes screen (Brief 7b): every string and number it
 * renders, every validation verdict, and the ONE function that builds the POST
 * body. Components wire these up; they never build copy, parse input, or
 * assemble a request themselves — the buildFeeConfirm / lib/commission.ts
 * precedent.
 *
 * Distinct from `lib/quote.ts`-adjacent Phase-8 code: `hooks/useQuote.ts` and
 * `components/quote/` belong to the student cost estimator, a different feature
 * that happens to share the word. Nothing here touches it.
 */

/** The one sentence this whole brick exists to make true. Rendered ONCE per screen. */
export const QUOTE_IMMUTABLE_NOTE =
  'A permanent record. To change anything, issue a new quote.';

/**
 * Who reaches the quote builder. Brief 8b re-based this on CAPABILITIES: it asks
 * what the user may do, not what they are called. See lib/capabilities.ts.
 *
 * Same six verdicts as the role gate it replaces — but the reasons are now the
 * mechanism, not a comment describing one:
 *  - `consultant` / `admin`: hold create_quotes + view_pricing, and have a
 *    tenant. In, exactly as before.
 *  - `super_admin` holds every capability, but has no tenant of its own, so every
 *    /quotes route answers 400 TENANT_REQUIRED by design (verified by execution,
 *    Brief 8b). Out on the TENANT, not on the role.
 *  - `offshore` holds create_quotes but NOT view_pricing, so the picker's own
 *    endpoint would 403 it. Out on the CAPABILITY, not the role — which is the
 *    point: grant them view_pricing and they are in, no code change.
 *  - `owner` / `accounts` lack create_quotes → the API answers 403.
 *
 * The server's create_quotes guard stays the real gate; this only decides what to
 * show. Kept as a named re-export rather than inlined at the call sites: it is
 * this module's published contract, and the name says which SCREEN is meant.
 */
export const canBuildQuotes = canSeeQuotes;

/** Structural: any row carrying the SERVER's net. Never a shape we compute. */
export type NetBearing = { netFeeCents?: number | null };

/**
 * The running total of the courses picked so far, summed from the SERVER's
 * netFeeCents — the browser adds server numbers, it never computes fee minus
 * scholarship (the pricing.tsx stance: "the net is never recomputed in the
 * browser").
 *
 * BUILDER ONLY, and INDICATIVE: prices are re-resolved and locked server-side at
 * POST, so this can legitimately differ from the saved quote. The list and detail
 * screens render the server's `totalNetFeeCents` and must never call this — a
 * QuoteListRow carries no lines[] to sum in any case.
 *
 * Empty → 0. The `?? 0` is defensive only: isAddable already refuses a null net.
 */
export function indicativeTotalCents(rows: readonly NetBearing[]): number {
  return rows.reduce((sum, r) => sum + (r.netFeeCents ?? 0), 0);
}

/**
 * Can this course go on a quote? A row with no fee locks nothing — the engine
 * rejects it outright ("has no agency fee as of …"), so the picker must not offer
 * it. Both fields are checked: no fee ⇒ agencyFee null AND netFeeCents null, and
 * `?? null` covers the optional `?` (the pricing.tsx precedent).
 *
 * A NEGATIVE net is deliberately ADDABLE. The server is the adjudicator: it
 * rejects it with a message naming the course. Blocking it here would make the
 * browser rule on money, and would make that rejection unreachable dead code.
 */
export function isAddable(row: PricingCourseRow): boolean {
  return row.agencyFee != null && (row.netFeeCents ?? null) != null;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * A quote's calendar date ('YYYY-MM-DD') → "14 May 2026".
 *
 * NOT lib/format.ts's formatDate: that runs the string through `new Date(...)`,
 * which reads a bare date as UTC midnight, so a viewer west of Greenwich renders
 * the PREVIOUS day — on 2026-01-01 it prints 2025, and there is no TZ pin in the
 * vitest config. `quoteDate` and the pricing list's `asOf` are calendar dates:
 * no time, no zone, and they must render identically everywhere.
 *
 * Same trap and same fix as formatCommissionDate; copied rather than imported so
 * a screen that must contain no commission does not import the commission module
 * (the ConfirmDialog duplication precedent).
 *
 * `createdAt` IS a real instant — use formatDate for it, and only for it.
 */
export function formatQuoteDate(date: string | null | undefined): string {
  if (!date) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) return '—';
  const month = MONTHS[Number(m[2]) - 1];
  if (!month) return '—';
  return `${Number(m[3])} ${month} ${m[1]}`;
}

/** "Subclass 500 · Student visa" — the Select's option label. */
export function visaSubclassLabel(v: VisaSubclass): string {
  return `Subclass ${v.subclass} · ${v.name}`;
}

/**
 * Render a STORED subclass. The column is plain text on purpose: the list is
 * Home Affairs policy that changes, and a locked quote must stay renderable after
 * a subclass is retired. An unknown code degrades to "Subclass 999" — never
 * blank, never a crash.
 */
export function visaSubclassDisplay(subclass: string): string {
  const match = VISA_SUBCLASSES.find((v) => v.subclass === subclass);
  return match ? visaSubclassLabel(match) : `Subclass ${subclass}`;
}

/** The client form's raw values — all strings, so nothing can be null. */
export type ClientFormValues = {
  name: string;
  email: string;
  phone: string;
  visaSubclass: string;
  nationality: string;
  notes: string;
};

export const emptyClientForm: ClientFormValues = {
  name: '',
  email: '',
  phone: '',
  visaSubclass: '',
  nationality: '',
  notes: '',
};

export type ClientFieldErrors = Partial<Record<keyof ClientFormValues, string>>;

/**
 * Deliberately PERMISSIVE — looser than the server's EmailSchema, never stricter.
 * The server is the gate; a client rule stricter than the server's would silently
 * block a legitimate address, which is the only failure mode that matters here.
 * Anything this lets through and the server refuses comes back as a verbatim 400
 * the screen shows.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Mirrors the server's QuoteClientSchema + CreateQuoteSchema.notes rules.
 *
 * Empty optionals are NOT errors — they are omitted from the body entirely (the
 * API rejects both `null` and `''` for them), so they can never be "too short".
 */
export function validateClientForm(values: ClientFormValues): ClientFieldErrors {
  const e: ClientFieldErrors = {};

  const name = values.name.trim();
  if (name === '') e.name = 'Client name is required';
  else if (name.length > 120) e.name = 'Client name must be 120 characters or fewer';

  const email = values.email.trim();
  if (email === '') e.email = 'Client email is required';
  else if (email.length > 254) e.email = 'Client email must be 254 characters or fewer';
  else if (!EMAIL_RE.test(email)) e.email = 'Enter a valid email address';

  const phone = values.phone.trim();
  if (phone !== '' && phone.length > 32) e.phone = 'Phone must be 32 characters or fewer';

  if (values.visaSubclass === '') e.visaSubclass = 'Select a visa subclass';
  else if (!VISA_SUBCLASSES.some((v) => v.subclass === values.visaSubclass)) {
    e.visaSubclass = 'Select a visa subclass';
  }

  const nationality = values.nationality.trim();
  if (nationality !== '' && nationality.length > 80) {
    e.nationality = 'Nationality must be 80 characters or fewer';
  }

  if (values.notes.trim().length > 2000) e.notes = 'Notes must be 2,000 characters or fewer';

  return e;
}

export function isClientFormValid(values: ClientFormValues): boolean {
  return Object.keys(validateClientForm(values)).length === 0;
}

/**
 * The POST /quotes body. This type IS the contract: exactly the keys the server's
 * `.strict()` schemas accept, and `notes` is TOP-LEVEL — nesting it inside
 * `client` would trip QuoteClientSchema.strict() with a 400. There is no
 * netFeeCents / quoteDate member, so emitting money or a date is a COMPILE error
 * rather than a test failure.
 */
export type CreateQuoteBody = {
  client: {
    name: string;
    email: string;
    visaSubclass: string;
    phone?: string; // OMITTED when absent — never null, never ''
    nationality?: string; // ditto
  };
  courseIds: string[];
  notes?: string; // ditto
};

/**
 * Form state → the exact body the server accepts. The ONLY body producer.
 *
 * OMIT, DON'T NULL. `phone`/`nationality`/`notes` are `.optional()` WITHOUT
 * `.nullable()`, so `phone: null` AND `phone: ''` are both 400s. The response
 * returns `phone: null`, so a response can never be round-tripped into a request
 * — which is why this takes all-string form values, not a QuoteView.
 *
 * The email is lowercased to match EmailSchema's own `.trim().toLowerCase()`, so
 * the review line shows the string the server will actually store.
 *
 * `courseIds` is the caller's pick order — which becomes the server's lineOrder
 * ("payload order = pathway order").
 */
export function buildCreateQuoteBody(
  values: ClientFormValues,
  courseIds: readonly string[],
): CreateQuoteBody {
  const client: CreateQuoteBody['client'] = {
    name: values.name.trim(),
    email: values.email.trim().toLowerCase(),
    visaSubclass: values.visaSubclass,
  };

  const phone = values.phone.trim();
  if (phone !== '') client.phone = phone;

  const nationality = values.nationality.trim();
  if (nationality !== '') client.nationality = nationality;

  const body: CreateQuoteBody = { client, courseIds: [...courseIds] };

  const notes = values.notes.trim();
  if (notes !== '') body.notes = notes;

  return body;
}
